import { useEffect, useState } from "react";
import { useAppDispatch } from "@/app/hooks";
import { fetchTrades } from "@/api/fetchTrades/fetchTradesApi";
import type { Trade } from "@/api/fetchTrades/types";
import {
  getQuickBooksTrade,
  createQuickBooksTrade,
  updateQuickBooksTrade,
} from "@/api/quickBooksTrade/quickBooksTradeApi";
import type { QuickBooksTradeResponse } from "@/api/quickBooksTrade/types";
import { useNotification } from "@/context/NotificationContext";
import { fetchIntegrationsStatusAction } from "../slices/integrationsStatusSlice";

export const useQuickBooksTradeConfiguration = (companyUuid: string) => {
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();

  const [trades, setTrades] = useState<Trade[]>([]);
  const [quickBooksTrade, setQuickBooksTrade] =
    useState<QuickBooksTradeResponse | null>(null);
  const [selectedTradeId, setSelectedTradeId] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadTradeData = async () => {
      setIsLoading(true);
      try {
        const [tradesResponse, currentTrade] = await Promise.all([
          fetchTrades(),
          getQuickBooksTrade(companyUuid),
        ]);
        setTrades(
          [...tradesResponse.data].sort((a, b) => a.name.localeCompare(b.name)),
        );
        setQuickBooksTrade(currentTrade);
      } catch (loadError) {
        console.error("Failed to load trade data:", loadError);
        showNotification("Error", "Failed to load trade data.", "error");
      } finally {
        setIsLoading(false);
      }
    };

    loadTradeData();
  }, [companyUuid]);

  const startEditing = () => {
    setSelectedTradeId(quickBooksTrade ? String(quickBooksTrade.tradeId) : "");
    setError("");
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setSelectedTradeId("");
    setError("");
    setIsEditing(false);
  };

  const requestSave = () => {
    if (!selectedTradeId) {
      setError("Please select a trade");
      return;
    }
    setError("");
    setIsConfirmOpen(true);
  };

  const closeConfirm = () => {
    setIsConfirmOpen(false);
  };

  const confirmSave = async () => {
    setIsSaving(true);
    try {
      const request = { tradeId: Number(selectedTradeId) };
      const result = quickBooksTrade
        ? await updateQuickBooksTrade(companyUuid, request)
        : await createQuickBooksTrade(companyUuid, request);

      setQuickBooksTrade(result);
      setSelectedTradeId("");
      setIsEditing(false);
      setIsConfirmOpen(false);
      dispatch(fetchIntegrationsStatusAction(false));
    } finally {
      setIsSaving(false);
    }
  };

  const selectedTradeName =
    trades.find((trade) => String(trade.id) === selectedTradeId)?.name ?? "";

  return {
    cancelEditing,
    closeConfirm,
    confirmSave,
    error,
    isConfirmOpen,
    isEditing,
    isLoading,
    isSaving,
    quickBooksTrade,
    requestSave,
    selectedTradeId,
    selectedTradeName,
    setSelectedTradeId,
    startEditing,
    trades,
  };
};
